/** The train band — shown ONLY while a train is open (the top bar owns
 *  selection and Create-train). Two faces:
 *   - CAPTURED train (accent): loco + title/meta + "✎ Modifying existing work"
 *     badge, "Updates on" level chips, + Levels / Cancel, and Apply + consequence.
 *   - FRESH train (accent): loco + "New train" + meta, a "📍 Select to place"
 *     toggle (arms place-mode), "Placed on" chips, and a lone Discard — no Apply.
 *  Below: the `trackSlot` — the REAL interactive train (a WbsBoardView scoped to
 *  the train), so no train logic is duplicated here. While a train is open a
 *  selection can still exist; the selection bar carries Delete / Clear and the
 *  train-only "Select from here".
 */
import React from "react";
import "./TrainBand.css";

export interface TrainBandLevelChip {
  index: number;
  label: string;
  isSource: boolean;
}

interface Props {
  trainKind: "captured" | "fresh";
  readOnly?: boolean;

  // --- with-train (shared) ----------------------------------------------
  title: string;
  meta: string;
  levelChips: TrainBandLevelChip[];
  onRemoveLevel?: (index: number) => void;
  /** The real interactive train track (a WbsBoardView scoped to the train). */
  trackSlot?: React.ReactNode;

  // --- captured train ---------------------------------------------------
  landLabel: string;
  canAddLevel?: boolean;
  onAddLevel?: () => void;
  onCancel?: () => void;
  onApply?: () => void;
  applyLabel: string;
  applyDisabled?: boolean;
  consequence: string;
  /** The train was applied and nothing's changed since — show a ✓ done state
   *  until the next train edit. */
  applied?: boolean;

  // --- fresh train ------------------------------------------------------
  placeArmed?: boolean;
  canPlace?: boolean;
  onTogglePlace?: () => void;
  onDiscard?: () => void;

  // --- selection (a selection can exist while the train is open) ---------
  // The bar shows for ANY selection (selCount) with Delete/Clear; "Select from
  // here" is shown only when train cars are selected (trainSelCount).
  selCount: number;
  onDelete?: () => void;
  onClear?: () => void;
  /** Count of selected TRAIN cars — gates the train-only "Select from here". */
  trainSelCount?: number;
  /** Extend the selection to every car at or after the earliest selected one. */
  onSelectFromHere?: () => void;
}

const TrainBand: React.FC<Props> = ({
  trainKind,
  readOnly = false,
  title,
  meta,
  levelChips,
  onRemoveLevel,
  trackSlot,
  landLabel,
  canAddLevel,
  onAddLevel,
  onCancel,
  onApply,
  applyLabel,
  applyDisabled = false,
  consequence,
  applied = false,
  placeArmed = false,
  canPlace = false,
  onTogglePlace,
  onDiscard,
  selCount,
  onDelete,
  onClear,
  trainSelCount = 0,
  onSelectFromHere,
}) => {
  const fresh = trainKind === "fresh";

  const chips = (
    <>
      {levelChips.map((c) => (
        <span key={c.index} className="train-band-chip">
          {c.label}
          {!c.isSource && !readOnly && onRemoveLevel && (
            <button
              type="button"
              className="train-band-chip-x"
              title={fresh ? "Remove this placement" : "Drop this level from the train"}
              onClick={() => onRemoveLevel(c.index)}
            >
              ×
            </button>
          )}
        </span>
      ))}
    </>
  );

  return (
    <div
      className={
        "train-band train-band--train" +
        (fresh && placeArmed ? " train-band--armed" : "")
      }
    >
      <div className="train-band-head">
        <span className="train-band-loco">🚆</span>
        <div className="train-band-titles">
          <span className="train-band-title">{title}</span>
          <span className="train-band-meta">{meta}</span>
        </div>

        {fresh ? (
          !readOnly &&
          onTogglePlace && (
            <button
              type="button"
              className={
                "train-band-place" +
                (placeArmed ? " train-band-place--armed" : "")
              }
              disabled={!placeArmed && !canPlace}
              title={
                placeArmed
                  ? "Stop placing"
                  : canPlace
                    ? "Place this train: click a level to drop the run"
                    : "Add work to the train first"
              }
              onClick={onTogglePlace}
            >
              {placeArmed ? "Cancel" : "📍 Select to place"}
            </button>
          )
        ) : (
          <span className="train-band-badge">✎ Modifying existing work</span>
        )}

        <div className="train-band-levels">
          {fresh ? (
            levelChips.length > 0 ? (
              <>
                <span className="train-band-landlabel">Placed on</span>
                {chips}
              </>
            ) : (
              <span className="train-band-landlabel train-band-landlabel--muted">
                Not placed yet
              </span>
            )
          ) : (
            <>
              <span className="train-band-landlabel">{landLabel}</span>
              {chips}
              {!readOnly && canAddLevel && onAddLevel && (
                <button
                  type="button"
                  className="train-band-addlevel"
                  title="Add another matching level to the train"
                  onClick={onAddLevel}
                >
                  + Levels
                </button>
              )}
            </>
          )}
        </div>

        <span className="train-band-spacer" />

        {fresh
          ? !readOnly &&
            onDiscard && (
              <button
                type="button"
                className="train-band-discard"
                title="Discard this train (placed work stays on the board)"
                onClick={onDiscard}
              >
                Discard
              </button>
            )
          : !readOnly && (
              <>
                {onCancel && (
                  <button
                    type="button"
                    className="train-band-cancel"
                    title="Discard this train (the activities stay on the board)"
                    onClick={onCancel}
                  >
                    ✕ Cancel
                  </button>
                )}
                {onApply && (
                  <>
                    <span className="train-band-divider" />
                    <div className="train-band-applycol">
                      <button
                        type="button"
                        className={
                          "train-band-apply" +
                          (applied ? " train-band-apply--done" : "")
                        }
                        disabled={applyDisabled || applied}
                        onClick={onApply}
                      >
                        {applied ? "✓ Applied" : applyLabel}
                      </button>
                      <span className="train-band-consequence">
                        {consequence}
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
      </div>

      {!readOnly && selCount > 0 && (
        <div className="train-band-selbar">
          <span className="train-band-selbar-count">
            <b>{selCount}</b> selected
          </span>
          {trainSelCount > 0 && onSelectFromHere && (
            <button
              type="button"
              className="train-band-selbar-btn"
              title="Select this and every car after it in the sequence"
              onClick={onSelectFromHere}
            >
              ⟶ Select from here
            </button>
          )}
          <span className="train-band-selbar-div" aria-hidden="true" />
          {onDelete && (
            <button
              type="button"
              className="train-band-selbar-btn train-band-selbar-btn--del"
              onClick={onDelete}
            >
              🗑 Delete
            </button>
          )}
          {onClear && (
            <button
              type="button"
              className="train-band-selbar-btn"
              onClick={onClear}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {trackSlot && <div className="train-band-track">{trackSlot}</div>}
    </div>
  );
};

export default TrainBand;
